/**
 * The task database. Two kinds of tasks, one list:
 * - inline tasks: `- [ ]` checkboxes in any page (text is the source of truth),
 * - task pages: pages in the tasks folder (or `type: task`) with properties.
 * Changes from task views are written back to the checkbox or the properties.
 */
import {
  addDays,
  dueToken,
  expandCapture,
  isOpen,
  nextOccurrence,
  parseTaskText,
  priorityFromWord,
  PRIORITY_WORD,
  statusFromChar,
  statusFromWord,
  STATUS_WORD,
  splitDateTime,
  TEMPLATE_PROP,
  toIsoDate,
  type TaskPriority,
  type TaskStatus,
} from '@nexus/core';
import {
  appendTask,
  applyTaskStatus,
  ensureTaskIds,
  findTaskItem,
  replaceTaskItemByBullet,
  setItemPriority,
  setItemToken,
  taskItemText,
  updateTaskItem,
} from '@nexus/editor';
import type { JSONContent } from '@tiptap/core';
import type { PageRecord, Store } from '@nexus/store';
import { loadPageJSON, reindexPage } from './content';
import { mutatePageDoc } from './docs';

export interface Task {
  id: string;
  kind: 'inline' | 'page';
  /** Page holding the checkbox (inline) or the task page itself. */
  pageId: string;
  /** Title of that page (inline) — where the task lives. */
  sourceTitle: string;
  parentId: string | null;
  position: number;
  title: string;
  status: TaskStatus;
  priority: TaskPriority | null;
  due: string | null;
  /** Time on the due date ("14:00"), if any. */
  dueTime: string | null;
  scheduled: string | null;
  start: string | null;
  doneAt: string | null;
  recurrence: string | null;
  /** Absolute reminders: YYYY-MM-DD or YYYY-MM-DDTHH:mm. */
  reminders: string[];
  /** `⏰ -1d` style: minutes before the due date. */
  relativeReminders: number[];
  tags: string[];
  projectId: string | null;
  category: string | null;
  owner: string | null;
}

type PageWithProps = PageRecord & { props: Record<string, unknown> };

export const today = () => toIsoDate(new Date());

const str = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));
const stripLink = (v: string) =>
  v
    .replace(/^\[\[|\]\]$/g, '')
    .split('|')[0]!
    .split('#')[0]!
    .trim();

/** First matching property (case-insensitive key). */
function prop(props: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const hit = Object.keys(props).find((k) => k.toLowerCase() === key);
    if (hit !== undefined && props[hit] !== null && props[hit] !== '') return props[hit];
  }
  return undefined;
}

/** Normalise "2026-10-05", "2026-10-05T09:00" or a Date to a date string. */
function dateOf(value: unknown): string | null {
  return dateTimeOf(value)?.date ?? null;
}

/** Date and optional time of a property value ("2026-10-05T14:00", "2026-10-05 14:00", a Date). */
function dateTimeOf(value: unknown): { date: string; time: string | null } | null {
  if (value instanceof Date) return { date: toIsoDate(value), time: null };
  const s = str(Array.isArray(value) ? value[0] : value);
  return s ? splitDateTime(s) : null;
}

/** Reminder properties of a task page: a date(time) or a relative "1d" / "-30m", or a list. */
function pageReminders(value: unknown): { reminders: string[]; relativeReminders: number[] } {
  const values = (Array.isArray(value) ? value : value === undefined ? [] : [value])
    .flatMap((v) => (v instanceof Date ? [toIsoDate(v)] : String(v).split(',')))
    .map((v) => v.trim())
    .filter(Boolean);
  const { reminders, relativeReminders } = parseTaskText(values.map((v) => `⏰ ${v}`).join(' '));
  return { reminders, relativeReminders };
}

/** Value to store in a `due` property: "2026-10-05" or "2026-10-05T14:00". */
const duePropValue = (due: string) => {
  const dt = splitDateTime(due);
  return dt ? (dt.time ? `${dt.date}T${dt.time}` : dt.date) : due;
};

/** Property names that hold the due date of a task page (Notion/Obsidian variants). */
const DUE_KEYS = ['due', 'due date', 'deadline', 'date', 'next due'];

/** A database template row (`nexus.template`) is no task or project itself. */
const isTemplate = (p: PageWithProps) => p.props[TEMPLATE_PROP] === true;

/** Values of a property as plain strings (a list, or one value; links unwrapped). */
const values = (v: unknown): string[] =>
  (Array.isArray(v) ? v : v === undefined || v === null ? [] : [v])
    .map((x) => stripLink(String(x)))
    .filter(Boolean);

/** `type: project`, also `[[Project]]` or a list that contains it. */
export const isProject = (p: PageWithProps) =>
  !isTemplate(p) &&
  values(prop(p.props, 'type')).some((t) => /^(project|projects|projecten)$/i.test(t));

async function specialIds(store: Store) {
  return {
    tasksParent: await store.getSetting('tasks_parent_id'),
    templatesParent: await store.getSetting('templates_parent_id'),
  };
}

export async function loadTasks(store: Store): Promise<Task[]> {
  const pages = await store.listPagesWithProps();
  const byId = new Map(pages.map((p) => [p.id, p]));
  // Projects by title and alias (the oldest wins when two share a name).
  const projectsByName = new Map<string, string>();
  for (const p of [...pages].filter(isProject).sort((a, b) => a.createdAt - b.createdAt))
    for (const name of [p.title, ...values(prop(p.props, 'aliases', 'alias'))]) {
      const key = name.toLowerCase();
      if (!projectsByName.has(key)) projectsByName.set(key, p.id);
    }
  const { tasksParent, templatesParent } = await specialIds(store);

  const ancestors = (id: string): PageWithProps[] => {
    const out: PageWithProps[] = [];
    let p = byId.get(id);
    while (p && out.length < 50) {
      out.push(p);
      p = p.parentId ? byId.get(p.parentId) : undefined;
    }
    return out;
  };
  const inTemplates = (id: string) =>
    !!templatesParent && ancestors(id).some((a) => a.id === templatesParent);
  /** A project named in a property: `[[Nexus]]`, `[[Projecten/Nexus]]`, an alias, a list. */
  const projectByName = (v: unknown) => {
    for (const name of values(v)) {
      const key = name.toLowerCase();
      const id = projectsByName.get(key) ?? projectsByName.get(key.split('/').pop()!.trim());
      if (id) return id;
    }
    return null;
  };
  /** Project from an explicit reference, else the nearest project page above. */
  const projectFor = (pageId: string, linkIds: string[] = []) => {
    const linked = linkIds.find((id) => {
      const p = byId.get(id);
      return p && isProject(p);
    });
    if (linked) return linked;
    for (const a of ancestors(pageId)) {
      if (isProject(a)) return a.id;
      const named = projectByName(prop(a.props, 'project', 'projects'));
      if (named) return named;
    }
    return null;
  };

  const templateRows = new Set(pages.filter(isTemplate).map((p) => p.id));
  const tasks: Task[] = [];
  for (const row of await store.listInlineTasks()) {
    if (inTemplates(row.pageId) || templateRows.has(row.pageId)) continue;
    const parsed = parseTaskText(row.text);
    const page = byId.get(row.pageId);
    tasks.push({
      id: row.id,
      kind: 'inline',
      pageId: row.pageId,
      sourceTitle: page?.title ?? '',
      parentId: row.parentId,
      position: row.position,
      title: parsed.title,
      status: statusFromChar(row.statusChar),
      priority: parsed.priority,
      due: parsed.due,
      dueTime: parsed.dueTime,
      scheduled: parsed.scheduled,
      start: parsed.start,
      doneAt: parsed.doneAt,
      recurrence: parsed.recurrence,
      reminders: parsed.reminders,
      relativeReminders: parsed.relativeReminders,
      tags: parsed.tags,
      projectId: projectFor(row.pageId, row.linkIds),
      category: null,
      owner: null,
    });
  }

  for (const page of pages) {
    const type = String(prop(page.props, 'type') ?? '');
    // Task-shaped pages (status + due date) count too, e.g. archived task folders.
    const shaped =
      prop(page.props, 'status') !== undefined &&
      prop(page.props, ...DUE_KEYS) !== undefined &&
      !/^(project|projects|projecten|daily|meeting|vergadering|klant|area|gebied)$/i.test(type);
    const isTaskPage =
      /^(task|taak|todo)$/i.test(type) ||
      (!!tasksParent && page.parentId === tasksParent) ||
      shaped;
    if (!isTaskPage || inTemplates(page.id) || isTemplate(page)) continue;
    const tagsProp = prop(page.props, 'tags');
    const due = dateTimeOf(prop(page.props, ...DUE_KEYS));
    const reminders = pageReminders(
      prop(page.props, 'reminder', 'reminders', 'herinnering', 'herinneringen'),
    );
    tasks.push({
      id: page.id,
      kind: 'page',
      pageId: page.id,
      sourceTitle: byId.get(page.parentId ?? '')?.title ?? '',
      parentId: null,
      position: 0,
      title: page.title,
      status: statusFromWord(prop(page.props, 'status')),
      priority: priorityFromWord(prop(page.props, 'priority')),
      due: due?.date ?? null,
      dueTime: due?.time ?? null,
      scheduled: dateOf(prop(page.props, 'scheduled')),
      start: dateOf(prop(page.props, 'start')),
      doneAt: dateOf(prop(page.props, 'done', 'completed')),
      recurrence: str(prop(page.props, ...RECUR_KEYS)),
      ...reminders,
      tags: Array.isArray(tagsProp) ? tagsProp.map(String) : [],
      projectId:
        projectByName(prop(page.props, 'project', 'projects')) ?? projectFor(page.parentId ?? ''),
      category: str(prop(page.props, 'category')),
      owner: str(prop(page.props, 'owner')),
    });
  }
  return tasks;
}

// ---- changes ----------------------------------------------------------------------

export interface TaskPatch {
  status?: TaskStatus;
  /** "2026-10-05" or with a time "2026-10-05 14:00" (see `withDueTime`). */
  due?: string | null;
  priority?: TaskPriority | null;
}

/** The key a property already uses on the page (keeps `Status` vs `status`). */
async function propKey(store: Store, pageId: string, ...keys: string[]): Promise<string> {
  const props = await store.getProps(pageId);
  for (const key of keys) {
    const hit = Object.keys(props).find((k) => k.toLowerCase() === key);
    if (hit) return hit;
  }
  return keys[0]!;
}

export async function updateTask(
  store: Store,
  task: Pick<Task, 'id' | 'kind' | 'pageId'>,
  patch: TaskPatch,
): Promise<void> {
  if (task.kind === 'page') {
    // A recurring task page stays the same page: it moves on to its next date,
    // and the done occurrence is listed under "Afgerond" at its bottom.
    if (patch.status === 'done') {
      const due = dateTimeOf(prop(await store.getProps(task.pageId), ...DUE_KEYS));
      if (await recurTaskPage(store, task.pageId)) {
        await recordCompletion(store, task.pageId, due?.date ?? null);
        return;
      }
    }
    if (patch.status !== undefined) {
      await store.setProp(
        task.pageId,
        await propKey(store, task.pageId, 'status'),
        STATUS_WORD[patch.status],
      );
    }
    if (patch.due !== undefined) {
      await store.setProp(
        task.pageId,
        await propKey(store, task.pageId, ...DUE_KEYS),
        patch.due ? duePropValue(patch.due) : undefined,
      );
    }
    if (patch.priority !== undefined) {
      const value = patch.priority ? PRIORITY_WORD[patch.priority] : undefined;
      await store.setProp(task.pageId, await propKey(store, task.pageId, 'priority'), value);
    }
    return;
  }
  await mutatePageDoc(store, task.pageId, (doc) =>
    updateTaskItem(doc, task.id, (item) => {
      let items = [item];
      if (patch.due !== undefined) {
        const dt = patch.due ? splitDateTime(patch.due) : null;
        const value = dt ? dueToken(dt.date, dt.time) : null;
        items = items.map((i) => setItemToken(i, '📅', value));
      }
      if (patch.priority !== undefined)
        items = items.map((i) => setItemPriority(i, patch.priority ?? null));
      if (patch.status !== undefined)
        items = items.flatMap((i) => applyTaskStatus(i, patch.status!, today()));
      return items;
    }),
  );
}

/** A new due date that keeps the task's time ("2026-10-07" + "14:00" → "2026-10-07 14:00"). */
export const withDueTime = (date: string | null, task: Pick<Task, 'dueTime'>) =>
  date ? dueToken(date, task.dueTime) : null;

const RECUR_KEYS = ['recurrence', 'repeat', 'herhaling'];

/** Days from date a to date b (both YYYY-MM-DD). */
const diffDays = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/**
 * "Next occurrence" of a recurring task page (Notion's recur button): the due
 * date moves to the next date after today (keeping its time) and the status
 * goes back to To do. Returns false when the page has no usable recurrence.
 */
/** The next date of a recurring task page, or null (no rule, or not understood). */
async function nextDate(store: Store, pageId: string, now: string) {
  const props = await store.getProps(pageId);
  const rule = str(prop(props, ...RECUR_KEYS));
  if (!rule) return null;
  const due = dateTimeOf(prop(props, ...DUE_KEYS));
  const whenDone = /when done|als klaar/i.test(rule);
  let next = nextOccurrence(rule, whenDone || !due ? now : due.date);
  // A long-overdue task jumps to the first date after today, not one step.
  for (let i = 0; next && next <= now && !whenDone && i < 1000; i++)
    next = nextOccurrence(rule, next);
  return next ? { next, due } : null;
}

const HISTORY_TITLE = 'Afgerond';

/** "2026-10-14" → "14/10/2026". */
const dmy = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');

/**
 * Note on the page that this occurrence was done: a line under the heading
 * "Afgerond" at the bottom (made when missing), the newest first.
 */
export async function recordCompletion(
  store: Store,
  pageId: string,
  due: string | null,
  now = today(),
): Promise<void> {
  const text = `✅ ${dmy(now)}${due ? ` (deadline ${dmy(due)})` : ''}`;
  const entry: JSONContent = {
    type: 'listItem',
    content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
  };
  await mutatePageDoc(store, pageId, (doc) => {
    const blocks = [...(doc.content ?? [])];
    const at = blocks.findIndex(
      (n) =>
        n.type === 'heading' &&
        (n.content ?? [])
          .map((c) => c.text ?? '')
          .join('')
          .trim() === HISTORY_TITLE,
    );
    if (at >= 0 && blocks[at + 1]?.type === 'bulletList') {
      const list = blocks[at + 1]!;
      blocks[at + 1] = { ...list, content: [entry, ...(list.content ?? [])] };
    } else {
      // A new section at the bottom (an empty last paragraph is replaced).
      while (
        blocks.length &&
        blocks[blocks.length - 1]!.type === 'paragraph' &&
        !blocks[blocks.length - 1]!.content?.length
      )
        blocks.pop();
      blocks.push(
        { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: HISTORY_TITLE }] },
        { type: 'bulletList', attrs: { tight: true, bullet: '-' }, content: [entry] },
      );
    }
    return { ...doc, content: blocks };
  });
}

export async function recurTaskPage(store: Store, pageId: string, now = today()): Promise<boolean> {
  const found = await nextDate(store, pageId, now);
  if (!found) return false;
  const { next, due } = found;
  await store.setProp(
    pageId,
    await propKey(store, pageId, ...DUE_KEYS),
    duePropValue(dueToken(next, due?.time)),
  );
  // Gepland and Startdatum move by the same number of days (as with task lines).
  if (due) {
    const props = await store.getProps(pageId);
    const shift = diffDays(due.date, next);
    for (const keys of [
      ['scheduled', 'gepland'],
      ['start', 'startdatum'],
    ]) {
      const date = dateOf(prop(props, ...keys));
      if (date)
        await store.setProp(pageId, await propKey(store, pageId, ...keys), addDays(date, shift));
    }
  }
  await store.setProp(pageId, await propKey(store, pageId, 'status'), STATUS_WORD.todo);
  return true;
}

export const toggleDone = (store: Store, task: Task) =>
  updateTask(store, task, { status: isOpen(task.status) ? 'done' : 'todo' });

// ---- special pages & new tasks ------------------------------------------------------------

/** A page remembered under a setting key; found by title first, created when missing. */
export async function ensureSpecialPage(
  store: Store,
  key: string,
  title: string,
  icon: string,
  opts: { anywhere?: boolean } = {},
): Promise<PageRecord> {
  const id = await store.getSetting(key);
  const existing = id ? await store.getPage(id) : null;
  if (existing && !existing.deleted && !existing.trashedAt) return existing;
  const pages = await store.listPages();
  const found = pages.find(
    (p) => p.title.toLowerCase() === title.toLowerCase() && (opts.anywhere || p.parentId === null),
  );
  const page = found ?? (await store.createPage({ title, icon }));
  await store.setSetting(key, page.id);
  return page;
}

export const inboxPage = (store: Store) =>
  ensureSpecialPage(store, 'inbox_page_id', 'Inbox', '📥', { anywhere: true });
export const tasksFolder = (store: Store) =>
  ensureSpecialPage(store, 'tasks_parent_id', 'Taken', '✅', { anywhere: true });

/** Add a checkbox task to the end of a page (quick capture). */
export async function addTaskToPage(store: Store, pageId: string, text: string): Promise<string> {
  let id = '';
  await mutatePageDoc(store, pageId, (doc) => {
    const r = appendTask(doc, text);
    id = r.taskId;
    return r.doc;
  });
  return id;
}

/** New task page in the tasks folder (a Notion-style database row). */
export async function createTaskPage(
  store: Store,
  input: {
    title: string;
    due?: string | null;
    priority?: TaskPriority | null;
    projectTitle?: string | null;
    tags?: string[];
  },
): Promise<PageRecord> {
  const folder = await tasksFolder(store);
  const page = await store.createPage({ title: input.title, parentId: folder.id });
  await store.setProp(page.id, 'status', STATUS_WORD.todo);
  if (input.priority) await store.setProp(page.id, 'priority', PRIORITY_WORD[input.priority]);
  if (input.due) await store.setProp(page.id, 'due', duePropValue(input.due));
  if (input.projectTitle) await store.setProp(page.id, 'project', `[[${input.projectTitle}]]`);
  if (input.tags?.length) await store.setProp(page.id, 'tags', input.tags);
  return page;
}

/**
 * Turn a checkbox task into a task page (Notion-style "open as page"): the page
 * gets the task's due date, priority, project, tags, recurrence and reminders,
 * and what was nested under the checkbox (subtasks, notes) becomes its content.
 * The checkbox itself becomes a bullet linking to the new page, so the task is
 * not listed twice. Returns the new page, or null when the task is gone.
 */
export async function promoteInlineTask(
  store: Store,
  task: Pick<Task, 'id' | 'pageId'>,
): Promise<PageRecord | null> {
  // The open editor's document (if any) is the latest; index it so the task is known.
  let doc: JSONContent | null = null;
  await mutatePageDoc(store, task.pageId, (d) => ((doc = d), null));
  if (!doc) return null;
  await reindexPage(store, task.pageId, doc);
  const item = findTaskItem(doc, task.id);
  const full = (await loadTasks(store)).find((t) => t.id === task.id && t.kind === 'inline');
  if (!item || !full) return null;
  const project = full.projectId ? await store.getPage(full.projectId) : null;
  // Shorthand still in the line (`@morgen`, `!!`) counts too.
  const fields = parseTaskText(expandCapture(taskItemText(item), today()));
  const title =
    fields.title
      .replace(/(^|\s)#[\p{L}\p{N}_\-/]+/gu, '$1')
      .replace(/\s+/g, ' ')
      .trim() || full.title;
  const page = await createTaskPage(store, {
    title,
    due: fields.due ? dueToken(fields.due, fields.dueTime) : null,
    priority: fields.priority,
    projectTitle: project?.title ?? null,
    tags: fields.tags,
  });
  if (full.status !== 'todo') await store.setProp(page.id, 'status', STATUS_WORD[full.status]);
  if (fields.recurrence) await store.setProp(page.id, 'recurrence', fields.recurrence);
  const reminders = [
    ...fields.reminders.map((r) => r.replace('T', ' ')),
    ...fields.relativeReminders.map((m) => `${m}m`),
  ];
  if (reminders.length) await store.setProp(page.id, 'Herinnering', reminders);
  const nested = (item.content ?? []).slice(1);
  if (nested.length) await mutatePageDoc(store, page.id, () => ({ type: 'doc', content: nested }));
  await mutatePageDoc(store, task.pageId, (doc) =>
    replaceTaskItemByBullet(doc, task.id, [
      { type: 'wikiLink', attrs: { pageId: page.id, target: page.title } },
    ]),
  );
  return page;
}

/**
 * Quick-capture text → task page fields: `Sessies inplannen @morgen 14u !! #training`
 * gives the title "Sessies inplannen", due tomorrow 14:00, high priority and a tag.
 */
export function taskPageFromCapture(text: string, todayIso: string) {
  const parsed = parseTaskText(expandCapture(text.trim(), todayIso));
  return {
    title: parsed.title
      .replace(/(^|\s)#[\p{L}\p{N}_\-/]+/gu, '$1')
      .replace(/\s+/g, ' ')
      .trim(),
    due: parsed.due ? dueToken(parsed.due, parsed.dueTime) : null,
    priority: parsed.priority,
    tags: parsed.tags,
  };
}

/**
 * One-time upgrade: give every checkbox an id and fill the task index.
 * Safe to run again; only pages with tasks are touched.
 */
export async function migrateTasks(
  store: Store,
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  const pages = await store.listPages();
  let touched = 0;
  for (const [i, page] of pages.entries()) {
    const json = await loadPageJSON(store, page.id);
    if (JSON.stringify(json).includes('"taskItem"')) {
      const { doc, changed } = ensureTaskIds(json);
      if (changed) await mutatePageDoc(store, page.id, () => doc);
      else await reindexPage(store, page.id, json);
      touched++;
    }
    onProgress?.(i + 1, pages.length);
  }
  return touched;
}

// ---- projects ----------------------------------------------------------------------------

export interface ProjectSummary {
  page: PageRecord;
  status: string | null;
  priority: TaskPriority | null;
  deadline: string | null;
  open: number;
  done: number;
  progress: number; // 0..1
  overdue: number;
}

export function summarizeProjects(
  pages: PageWithProps[],
  tasks: Task[],
  now = today(),
): ProjectSummary[] {
  return pages.filter(isProject).map((page) => {
    const own = tasks.filter((t) => t.projectId === page.id && t.status !== 'cancelled');
    const done = own.filter((t) => t.status === 'done').length;
    return {
      page,
      status: str(prop(page.props, 'status')),
      priority: priorityFromWord(prop(page.props, 'priority')),
      deadline: dateOf(prop(page.props, 'due', 'deadline')),
      open: own.length - done,
      done,
      progress: own.length ? done / own.length : 0,
      overdue: own.filter((t) => isOpen(t.status) && t.due !== null && t.due < now).length,
    };
  });
}

// ---- archive -------------------------------------------------------------------------------

const ARCHIVE_TITLE = 'Archief';

/**
 * Move the finished checkbox tasks (done or cancelled, with what is nested under
 * them) of a page to its "Archief" subpage, under a heading with today's date.
 * The page keeps only what is still open. Returns how many tasks moved.
 */
export async function archiveDoneTasks(
  store: Store,
  pageId: string,
  now = today(),
): Promise<number> {
  const finished = (item: JSONContent) =>
    item.attrs?.checked === true || ['x', 'X', '-'].includes(String(item.attrs?.status ?? ' '));
  const moved: JSONContent[] = [];
  await mutatePageDoc(store, pageId, (doc) => {
    const walk = (n: JSONContent): JSONContent | null => {
      if (n.type === 'taskList') {
        const keep = (n.content ?? []).filter((item) => {
          if (item.type === 'taskItem' && finished(item)) {
            moved.push(item);
            return false;
          }
          return true;
        });
        return keep.length ? { ...n, content: keep.map((c) => walk(c) ?? c) } : null;
      }
      if (!n.content) return n;
      const content = n.content.map(walk).filter((c): c is JSONContent => c !== null);
      return { ...n, content };
    };
    const next = walk(doc);
    if (!moved.length || !next) return null;
    return next.content?.length ? next : { ...next, content: [{ type: 'paragraph' }] };
  });
  if (!moved.length) return 0;

  const children = (await store.listPages()).filter(
    (p) => p.parentId === pageId && p.title === ARCHIVE_TITLE && !p.trashedAt,
  );
  const archive =
    children[0] ?? (await store.createPage({ title: ARCHIVE_TITLE, parentId: pageId, icon: '🗄️' }));
  await mutatePageDoc(store, archive.id, (doc) => ({
    ...doc,
    content: [
      { type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: now }] },
      { type: 'taskList', attrs: { tight: true, bullet: '-' }, content: moved },
      ...(doc.content ?? []).filter((n) => !(n.type === 'paragraph' && !n.content?.length)),
    ],
  }));
  return moved.length;
}
